import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";

import { applicationService } from "../services/applicationService";

// What happened to this candidate, and who did it.
//
// ─── WHAT MAKES A LOG READABLE ──────────────────────────────────────────────
//
// The first version of this rendered one line per row: label, operator, stamp,
// note. Correct, and unreadable — because `notes` is written by nine different
// places for a developer tailing a log, so the reader was scanning past blob
// prefixes, SMTP message-ids and `notice_period_days=25` to find the two or
// three words that mattered.
//
// Four things fix that, and the first is the important one:
//
//   1. A PERSON'S WORDS ARE NOT THE SYSTEM'S DESCRIPTION. The server now
//      returns `note` (what somebody typed) and `detail` (what the system did)
//      as separate fields. A note is quoted and given weight; a detail is small
//      and grey. Scanning for "what did somebody actually say about her" is now
//      one glance rather than a read.
//
//   2. DATES BECOME DAYS. Every row carried a full timestamp, so eleven rows
//      meant eleven dates to compare. They are grouped under Today / Yesterday
//      / a weekday, and rows inside a group show only a time.
//
//   3. LONG TEXT IS CLAMPED. The AI screening note is 400 characters of model
//      prose. Worth keeping, not worth putting in front of everything else.
//
//   4. PROBLEMS LOOK LIKE PROBLEMS. A rejection with no reason, and an email
//      that never arrived, are the two things somebody is looking for when they
//      open this. They are amber, not another grey line.

const C = {
  ink: "#101828",
  body: "#344054",
  muted: "#475467",
  faint: "#667085",
  hair: "#e4e7ec",
  wash: "#f9fafb",
  accent: "#037e8f",
  danger: "#b42318",
  warnBg: "#fffaeb",
  warnLine: "#fedf89",
  warnInk: "#b54708",
};
const FONT = "'Plus Jakarta Sans', 'Inter', sans-serif";

// Types that read as a setback, so the trail can be scanned for them. Colour is
// an aid only — the label still says what happened.
const NEGATIVE = new Set(["REJECTED"]);

// Above this, a detail is collapsed behind "Show more". Chosen to fit about
// three lines in the drawer: enough to tell whether it is worth opening.
const CLAMP = 180;

const pad = (n) => String(n).padStart(2, "0");

function dayKey(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// "Today" / "Yesterday" / "Mon, 12 Aug 2026".
//
// Relative for the two days somebody is most likely to be asking about, and
// absolute beyond that — "4 days ago" forces the reader to do arithmetic to
// work out which day that actually was.
function dayLabel(d) {
  const today = new Date();
  const t = dayKey(today);
  const yd = new Date(today);
  yd.setDate(yd.getDate() - 1);
  const k = dayKey(d);
  if (k === t) return "Today";
  if (k === dayKey(yd)) return "Yesterday";
  return d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: d.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
}

function timeLabel(d) {
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
}

// One "by" chip, in three states that mean genuinely different things.
//
//   a name        somebody did this
//   "system"      the pipeline did it — an AI screening, an automated email.
//                 Nobody is missing.
//   "not recorded" somebody did it, before the system recorded who. This is a
//                 gap in the record, and it should not look like the row above.
function ByChip({ entry }) {
  if (entry.operator) {
    return (
      <Tooltip title={entry.operator.role || "operator"}>
        <Chip
          size="small"
          icon={<PersonOutlineRoundedIcon sx={{ fontSize: 13 }} />}
          label={entry.operator.name}
          sx={{
            height: 20,
            fontFamily: FONT,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: C.wash,
            border: `1px solid ${C.hair}`,
            "& .MuiChip-icon": { color: C.faint, ml: 0.5 },
          }}
        />
      </Tooltip>
    );
  }
  if (entry.by_operator_expected) {
    return (
      <Tooltip title="This happened before the system recorded who performed each action">
        <Chip
          size="small"
          label="who: not recorded"
          sx={{
            height: 20,
            fontFamily: FONT,
            fontSize: 11,
            fontStyle: "italic",
            bgcolor: C.warnBg,
            border: `1px solid ${C.warnLine}`,
            color: C.warnInk,
          }}
        />
      </Tooltip>
    );
  }
  return (
    <Chip
      size="small"
      icon={<SmartToyOutlinedIcon sx={{ fontSize: 13 }} />}
      label="system"
      sx={{
        height: 20,
        fontFamily: FONT,
        fontSize: 11,
        bgcolor: C.wash,
        border: `1px solid ${C.hair}`,
        color: C.faint,
        "& .MuiChip-icon": { color: C.faint, ml: 0.5 },
      }}
    />
  );
}

// The system's description of what happened. Small, grey, clamped.
function Detail({ text, warn }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = text.length > CLAMP;
  const shown = open || !long ? text : `${text.slice(0, CLAMP).trimEnd()}…`;

  return (
    <Box sx={{ mt: 0.4 }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: 12,
          lineHeight: 1.65,
          color: warn ? C.warnInk : C.faint,
          maxWidth: 680,
        }}
      >
        {warn && (
          <WarningAmberRoundedIcon
            sx={{ fontSize: 13, mr: 0.5, mb: "-2px", color: C.warnInk }}
          />
        )}
        {shown}
      </Typography>
      {long && (
        <Button
          onClick={() => setOpen((v) => !v)}
          size="small"
          sx={{
            p: 0,
            minWidth: 0,
            mt: 0.2,
            textTransform: "none",
            fontFamily: FONT,
            fontSize: 11.5,
            fontWeight: 600,
            color: C.accent,
          }}
        >
          {open ? "Show less" : "Show more"}
        </Button>
      )}
    </Box>
  );
}

export default function CandidateHistory({
  applicationId,
  candidateName,
  onChanged,
}) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [noteError, setNoteError] = useState("");
  // Newest first by default: this is a log, and the action being questioned is
  // almost always the most recent one. Reversible, because a candidate's
  // history also reads as a story and a story starts at the beginning.
  const [newestFirst, setNewestFirst] = useState(true);

  const load = useCallback(async () => {
    if (!applicationId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await applicationService.activityTrail(applicationId);
      const body = res?.data;
      if (!body?.success) throw new Error(body?.statusMessage || "Could not load the history.");
      setState({ loading: false, error: null, data: body.data });
    } catch (e) {
      setState({
        loading: false,
        error:
          e?.response?.data?.statusMessage || e?.message || "Could not load the history.",
        data: null,
      });
    }
  }, [applicationId]);

  // Mounted only while the Log & History tab is selected, so this is the lazy
  // fetch — no request for a candidate nobody opened the tab on.
  useEffect(() => {
    load();
  }, [load]);

  // Memoised, not `state.data?.entries || []`. That fallback allocates a fresh
  // array on every render, so both useMemos below saw a changed dependency each
  // time and regrouped the whole trail on every keystroke in the note box.
  const entries = useMemo(() => state.data?.entries || [], [state.data]);
  const unattributed = state.data?.unattributed || 0;
  const unexplained = useMemo(
    () => entries.filter((e) => e.unexplained).length,
    [entries],
  );

  // Grouped by day, in the chosen direction. The server returns oldest first.
  const groups = useMemo(() => {
    const list = newestFirst ? [...entries].reverse() : entries;
    const out = [];
    for (const e of list) {
      const d = new Date(e.at);
      const key = Number.isNaN(d.getTime()) ? "unknown" : dayKey(d);
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(e);
      else out.push({ key, date: d, items: [e] });
    }
    return out;
  }, [entries, newestFirst]);

  const submitNote = async () => {
    const text = note.trim();
    if (!text) {
      setNoteError("Write something first.");
      return;
    }
    setSaving(true);
    setNoteError("");
    try {
      await applicationService.addNote(applicationId, text);
      setNote("");
      await load();
      onChanged?.();
    } catch (e) {
      setNoteError(
        e?.response?.data?.statusMessage || e?.message || "Could not save the note.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
      >
        <HistoryRoundedIcon sx={{ fontSize: 15, color: C.accent }} />
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: 11,
            fontWeight: 750,
            letterSpacing: 0.8,
            color: C.accent,
          }}
        >
          EVERYTHING THAT HAPPENED
        </Typography>
        {!state.loading && entries.length > 0 && (
          <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: C.faint }}>
            {entries.length} action{entries.length === 1 ? "" : "s"}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {entries.length > 1 && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={newestFirst ? "new" : "old"}
            onChange={(_, v) => {
              if (v) setNewestFirst(v === "new");
            }}
            sx={{
              "& .MuiToggleButton-root": {
                py: 0.15,
                px: 1,
                textTransform: "none",
                fontFamily: FONT,
                fontSize: 11,
                fontWeight: 600,
                color: C.faint,
                borderColor: C.hair,
                "&.Mui-selected": {
                  color: C.accent,
                  bgcolor: "#e6f4f6",
                  "&:hover": { bgcolor: "#d9eef1" },
                },
              },
            }}
          >
            <ToggleButton value="new">Newest first</ToggleButton>
            <ToggleButton value="old">Oldest first</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {state.loading && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={14} sx={{ color: C.faint }} />
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.faint }}>
            Loading history…
          </Typography>
        </Stack>
      )}

      {state.error && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 2, fontFamily: FONT, fontSize: 12.5, mb: 2 }}
          action={
            <Button size="small" onClick={load} sx={{ textTransform: "none" }}>
              Retry
            </Button>
          }
        >
          {state.error}
        </Alert>
      )}

      {/* The two things worth flagging up front, said once rather than repeated
          on every affected row. Both are facts about the RECORD, not about the
          people in it. */}
      {!state.loading && (unattributed > 0 || unexplained > 0) && (
        <Alert
          severity="info"
          icon={<WarningAmberRoundedIcon sx={{ fontSize: 18 }} />}
          sx={{ borderRadius: 2, fontFamily: FONT, fontSize: 12.5, mb: 2 }}
        >
          {unexplained > 0 && (
            <>
              {unexplained} decision{unexplained === 1 ? "" : "s"} here{" "}
              {unexplained === 1 ? "has" : "have"} no reason recorded.{" "}
            </>
          )}
          {unattributed > 0 && (
            <>
              {unattributed} action{unattributed === 1 ? "" : "s"} predate
              operator tracking, so nobody is named against{" "}
              {unattributed === 1 ? "it" : "them"}.{" "}
            </>
          )}
          Everything from now on records both.
        </Alert>
      )}

      {!state.loading && !state.error && entries.length === 0 && (
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.faint }}>
          Nothing recorded yet beyond the application itself.
        </Typography>
      )}

      {groups.map((g) => (
        <Box key={g.key} sx={{ mb: 2.5 }}>
          {/* The day, once. Eleven rows used to mean eleven full timestamps to
              compare against each other. */}
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: 0.7,
              textTransform: "uppercase",
              color: C.faint,
              mb: 1.2,
            }}
          >
            {g.key === "unknown" ? "Date unknown" : dayLabel(g.date)}
          </Typography>

          <Box sx={{ position: "relative", pl: 2.5 }}>
            <Box
              sx={{
                position: "absolute",
                left: 4,
                top: 6,
                bottom: 6,
                width: "1px",
                bgcolor: C.hair,
              }}
            />
            <Stack spacing={1.75}>
              {g.items.map((e) => {
                const negative = NEGATIVE.has(e.type);
                const warn = e.unexplained || e.undelivered;
                return (
                  <Box key={e.id} sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        position: "absolute",
                        left: -20,
                        top: 5,
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        bgcolor: negative ? C.danger : warn ? C.warnInk : C.accent,
                        border: "2px solid #fff",
                      }}
                    />

                    <Stack
                      direction="row"
                      spacing={0.85}
                      alignItems="center"
                      flexWrap="wrap"
                      sx={{ rowGap: 0.5 }}
                    >
                      {/* Time only. The day is the group header above. */}
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: 11.5,
                          fontVariantNumeric: "tabular-nums",
                          color: C.faint,
                          minWidth: 52,
                        }}
                      >
                        {Number.isNaN(new Date(e.at).getTime())
                          ? "—"
                          : timeLabel(new Date(e.at))}
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: 13,
                          fontWeight: 700,
                          color: negative ? C.danger : C.ink,
                        }}
                      >
                        {e.label}
                      </Typography>

                      {e.to_stage && (
                        <Typography
                          sx={{ fontFamily: FONT, fontSize: 12.5, color: C.muted }}
                        >
                          {e.from_stage
                            ? `${e.from_stage} → ${e.to_stage}`
                            : `→ ${e.to_stage}`}
                        </Typography>
                      )}

                      {/* An AI band, or the verdict a human decision overrode. */}
                      {e.verdict && (
                        <Chip
                          size="small"
                          label={e.verdict.replace(/_/g, " ")}
                          sx={{
                            height: 19,
                            fontFamily: FONT,
                            fontSize: 10.5,
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            bgcolor: "#eef4ff",
                            border: "1px solid #c7d7fe",
                            color: "#3538cd",
                          }}
                        />
                      )}

                      {e.overrides_ai && (
                        <Tooltip title="A person disagreed with the AI's verdict and recorded why">
                          <Chip
                            size="small"
                            label="overrode the AI"
                            sx={{
                              height: 19,
                              fontFamily: FONT,
                              fontSize: 10.5,
                              fontWeight: 700,
                              bgcolor: C.warnBg,
                              border: `1px solid ${C.warnLine}`,
                              color: C.warnInk,
                            }}
                          />
                        </Tooltip>
                      )}

                      <ByChip entry={e} />
                    </Stack>

                    {/* The system's description: small and grey, because it is
                        context rather than the point. */}
                    <Box sx={{ pl: "60px" }}>
                      <Detail text={e.detail} warn={warn} />

                      {/* A PERSON'S OWN WORDS. Quoted and given weight — this is
                          what somebody opens the log to find, and in the first
                          version it looked identical to a message-id. */}
                      {e.note && (
                        <Box
                          sx={{
                            mt: 0.75,
                            display: "flex",
                            gap: 0.75,
                            alignItems: "flex-start",
                            bgcolor: negative ? "#fffbfa" : C.wash,
                            border: `1px solid ${negative ? "#fecdca" : C.hair}`,
                            borderRadius: 1.5,
                            px: 1.25,
                            py: 0.85,
                            maxWidth: 680,
                          }}
                        >
                          <FormatQuoteRoundedIcon
                            sx={{
                              fontSize: 15,
                              color: negative ? "#f97066" : C.faint,
                              flexShrink: 0,
                              mt: "1px",
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: 12.5,
                              lineHeight: 1.7,
                              color: C.body,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {e.note}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      ))}

      <Divider sx={{ my: 2.5, borderColor: C.hair }} />

      {/* A note on its own. NOTE_ADDED has been a valid activity type since the
          first hiring migration with nothing able to write it, so "spoke to her,
          she is on notice until the 20th" had nowhere to live. */}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            if (noteError) setNoteError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submitNote();
            }
          }}
          placeholder={
            candidateName
              ? `Add a note about ${String(candidateName).split(" ")[0]}…`
              : "Add a note…"
          }
          size="small"
          fullWidth
          multiline
          maxRows={4}
          disabled={saving}
          error={Boolean(noteError)}
          helperText={noteError || "Recorded against your name. Ctrl+Enter to save."}
          inputProps={{ maxLength: 500 }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" },
            "& .MuiInputBase-input": { fontFamily: FONT, fontSize: 12.5 },
            "& .MuiFormHelperText-root": { fontFamily: FONT, fontSize: 11 },
          }}
        />
        <Button
          onClick={submitNote}
          disabled={saving || !note.trim()}
          variant="outlined"
          size="small"
          startIcon={
            saving ? (
              <CircularProgress size={13} sx={{ color: C.faint }} />
            ) : (
              <NoteAddOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            mt: 0.25,
            flexShrink: 0,
            textTransform: "none",
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: 12.5,
            borderRadius: 2,
            borderColor: C.hair,
            color: C.body,
            "&:hover": { borderColor: C.accent, color: C.accent, bgcolor: "#fff" },
          }}
        >
          {saving ? "Saving…" : "Add note"}
        </Button>
      </Stack>
    </Box>
  );
}
